import { useQuery } from '@tanstack/react-query';
import { type ReactElement } from 'react';

import { MetaBox } from '@/components/admin/MetaBox';
import {
  AUDIENCE_LABELS,
  CONDITION_LABELS,
  TRIGGER_BASE_LABELS,
  formatOffset,
} from '@/features/notifications/format';
import { previewSchedulesFromType } from '@/features/notifications/api';
import { formatDateTime } from '@/lib/datetime';

function toIsoOrNull(local: string): string | null {
  if (!local) return null;
  const date = new Date(local);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/** 新規登録時に、種別プリセットから「この回に並ぶメール」を見せる（D-247）。予約そのものは登録後。 */
export function EventNotificationPlanPreview({
  typeId,
  startsAt,
  endsAt,
}: {
  typeId: string;
  startsAt: string;
  endsAt: string;
}): ReactElement | null {
  const startsIso = toIsoOrNull(startsAt);
  const endsIso = toIsoOrNull(endsAt);
  const query = useQuery({
    queryKey: ['notification-preview-from-type', typeId, startsIso, endsIso],
    queryFn: () =>
      previewSchedulesFromType({
        type_id: typeId,
        starts_at: startsIso,
        ends_at: endsIso,
      }),
    enabled: Boolean(typeId),
  });

  if (!typeId) return null;

  return (
    <MetaBox title="この回のメール（登録すると予約されます）">
      <p className="mb-3 text-wptext-muted">
        種類ごとの自動配信ルールから、この回に並ぶ案内です。登録したあとに、イベント詳細の「通知」タブでこの回だけ時刻を変えたり、1通足したり、止めたりできます。下書きのままだと、時刻が来ても送らずスキップします。
      </p>
      {query.isPending && <p className="text-wptext-muted">読み込み中…</p>}
      {query.isError && (
        <p className="text-wpnotice-error">この回のメール一覧を取得できませんでした。</p>
      )}
      {query.data && query.data.items.length === 0 && (
        <p className="text-wptext-muted">
          {query.data.preset_name
            ? '開催に紐づく自動配信はありません（毎月・毎週の全体配信は、この回の一覧には出ません）。'
            : 'この種類には自動配信のルールが付いていません。登録後、通知タブからこの回だけ足せます。'}
        </p>
      )}
      {query.data && query.data.items.length > 0 && (
        <div className="overflow-x-auto">
          {query.data.preset_name && (
            <p className="mb-2 text-wptext-muted">ルールの束: {query.data.preset_name}</p>
          )}
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-wpborder bg-wpcanvas/60">
                <th className="px-2 py-1.5">文面</th>
                <th className="px-2 py-1.5">宛先</th>
                <th className="px-2 py-1.5">タイミング</th>
                <th className="px-2 py-1.5">この回の配信時刻</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((item) => {
                const offsetLabel = formatOffset(item.offset);
                const whenRule = [
                  TRIGGER_BASE_LABELS[item.trigger_base] ?? item.trigger_base,
                  offsetLabel,
                  item.at ? `${item.at}` : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <tr key={item.rule_key} className="border-b border-wpborder">
                    <td className="px-2 py-1.5">
                      {item.template_name ?? item.template_code}
                      {item.condition && (
                        <span className="ml-1 text-wptext-muted">
                          （{CONDITION_LABELS[item.condition] ?? item.condition}）
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {AUDIENCE_LABELS[item.audience_key] ?? item.audience_key}
                    </td>
                    <td className="px-2 py-1.5">{whenRule}</td>
                    <td className="px-2 py-1.5">
                      {item.skip_reason
                        ? item.skip_reason
                        : item.scheduled_for
                          ? formatDateTime(item.scheduled_for)
                          : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </MetaBox>
  );
}
